// Live tracking protocol v1 (the API's /live-docs): the shapes below mirror
// its TrackedVehicle and VehicleTelemetry, with fields the feed may not have
// yet typed as nullable rather than invented.

export type VehicleStatus = "moving" | "idle" | "stopped" | "offline";

/** The Asset List's status filter: a live status, or an asset with no
 *  device. A device that never sent a position counts as offline. */
export type VehicleListStatus = VehicleStatus | "no_device";

export interface Coordinate {
  readonly latitude: number;
  readonly longitude: number;
}

/** A visible map area, in degrees. `east` below `west` crosses ±180°. */
export interface GeoBounds {
  readonly north: number;
  readonly south: number;
  readonly east: number;
  readonly west: number;
}

export interface NamedRef {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
}

/** The asset's active driver or supervisor. */
export interface VehicleContact {
  readonly id: string;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
}

/** What a sensor reading measures; `STATE` is on/off, open/closed. */
export type SensorQuantity = "TEMPERATURE" | "HUMIDITY" | "WEIGHT" | "STATE";

/** One probe of a sensor configured on the device, as the newest record
 *  (or the playback point) read it. */
export interface SensorReading {
  readonly sensorId: string;
  /** TEMPERATURE, WEIGHT, DOOR_STATUS, PTO, SEAT_BELT, REFRIGERATOR_STATE,
   *  HEADLIGHTS, SOS or IBUTTON. */
  readonly sensorType: string;
  /** Which probe or IO; with `sensorId`, stable across records. */
  readonly probe: string;
  /** The probe's configured name, else the sensor's; `null` with neither. */
  readonly label: string | null;
  readonly quantity: SensorQuantity;
  /** For STATE: 1 on / open / engaged, 0 off / closed. `null` while an
   *  assigned sensor hasn't reported a reading. */
  readonly value: number | null;
  /** e.g. °C, %, kg; `null` for STATE. */
  readonly unit: string | null;
  /** STATE only: the configuration's name for the value (Closed / مغلق). */
  readonly stateLabel: { readonly en: string | null; readonly ar: string | null } | null;
}

export interface VehicleTelemetry extends Coordinate {
  /** Increasing within one stream; a sequence that isn't newer is ignored. */
  readonly sequence: number;
  /** Device record time (UTC). */
  readonly recordedAt: string;
  /** When the shown position was fixed by GPS. */
  readonly gpsTime: string;
  /** When the server last heard from the device; `null` when unknown. */
  readonly lastCommunicationAt: string | null;
  /** Degrees; `null` when the device sent none (drawn as a plain pin). */
  readonly heading: number | null;
  readonly speedKph: number;
  /** Derived by the backend. */
  readonly status: VehicleStatus;
  /** `null` when never reported. */
  readonly ignitionOn: boolean | null;
  /** `null` when never reported. */
  readonly odometerKm: number | null;
  /** One entry per probe; empty when the device has no sensors or the
   *  record carries none. */
  readonly sensors: readonly SensorReading[];
}

export interface TrackedVehicle {
  /** The asset's id. */
  readonly id: string;
  readonly name: string;
  readonly nameAr: string;
  readonly code: string;
  /** May be empty. */
  readonly plateNumber: string;
  /** Empty until the backend resolves addresses. */
  readonly locationLabel: string;
  readonly assetType: NamedRef | null;
  readonly account: NamedRef | null;
  readonly branch: NamedRef | null;
  /** The device's IMEI; `null` with no device. */
  readonly imei: string | null;
  readonly hasDevice: boolean;
  /** A device of the asset is a camera that can stream live video: the
   *  camera action works only then. */
  readonly hasCamera: boolean;
  /** That camera device, for the live video endpoints; `null` without one. */
  readonly cameraDeviceId: string | null;
  /** How that camera streams, `RTMP` or `JT1078` (as sent); only a JT1078
   *  camera can be talked to. `null` without a camera. */
  readonly cameraVideoTransport: string | null;
  readonly driver: VehicleContact | null;
  readonly supervisor: VehicleContact | null;
  /** `null` until a device of the asset reports a valid position -- such a
   *  vehicle is listed but not on the map. */
  readonly telemetry: VehicleTelemetry | null;
}

/** A vehicle with a position -- the only kind the map draws. */
export interface PositionedVehicle extends TrackedVehicle {
  readonly telemetry: VehicleTelemetry;
}

export function isPositioned(vehicle: TrackedVehicle): vehicle is PositionedVehicle {
  return vehicle.telemetry !== null;
}

export interface PlaybackPoint extends VehicleTelemetry {
  readonly locationLabel: string;
  /** The vehicle battery, volts; `null` when the device didn't record it. */
  readonly battery: number | null;
  /** The alerts fired from this point until the next one, oldest first
   *  (pinned by the backend to the last point at or before each). */
  readonly alerts: readonly PlaybackAlert[];
}

/** The time range a playback covers (ISO timestamps, end exclusive of nothing
 *  -- both ends inclusive). */
export interface PlaybackRange {
  readonly from: string;
  readonly to: string;
}

/**
 * One movement/activity block of the history (S6 scenario 2): consecutive
 * GPS records grouped by status, as the backend is expected to send them.
 */
export interface PlaybackSegment {
  readonly id: string;
  readonly status: VehicleStatus;
  readonly startAt: string;
  readonly endAt: string;
  /** Address at the segment's start/end, when the backend resolved one. */
  readonly startLocation: string | null;
  readonly endLocation: string | null;
  /** Where it starts and ends: shown when there's no address (the backend
   *  says to show the coordinates then). */
  readonly startPoint: Coordinate;
  readonly endPoint: Coordinate;
  /** km/h; `null` when there isn't enough data to compute it (shown as N/A). */
  readonly averageSpeedKph: number | null;
  /** How many alerts fired during it. */
  readonly alertCount: number;
}

/** A point's street address in both languages; each `null` when none is
 *  known there (or not looked up yet). */
export interface PlaceAddress {
  readonly en: string | null;
  readonly ar: string | null;
}

/**
 * An alert the vehicle fired during a playback, as the playback endpoint
 * carries it (its Alert History row's general fields; the details come from
 * `GET /alerts/history/:id`).
 */
export interface PlaybackAlert {
  /** The Alert History row's id. */
  readonly id: string;
  readonly occurredAt: string;
  /** `occurredAt` in ms, for placing it on the playback's clock. */
  readonly occurredMs: number;
  readonly alertType: string;
  readonly alertNameEn: string;
  readonly alertNameAr: string;
  readonly triggeredValueEn: string;
  readonly triggeredValueAr: string;
  /** Where it fired; the point it's pinned to when the alert has none. */
  readonly position: Coordinate;
  /** Whether `position` is the alert's own location (else its point's). */
  readonly hasOwnLocation: boolean;
}

/** Real recorded points (chronological) plus their segments, for one asset
 *  and one range, and the alerts fired meanwhile (by time). */
export interface PlaybackHistory {
  readonly points: readonly PlaybackPoint[];
  readonly segments: readonly PlaybackSegment[];
  readonly alerts: readonly PlaybackAlert[];
}

export interface TrackingSnapshot {
  readonly vehicles: readonly TrackedVehicle[];
  readonly capturedAt: string;
}

export interface VehicleTelemetryEvent {
  readonly vehicleId: string;
  readonly telemetry: VehicleTelemetry;
}

/** What the live feed follows: the company (Level 1 must name one; a client
 *  user's own company always applies), the visible area (omitted: every
 *  vehicle in scope) and assets followed wherever they are. */
export interface TrackingScope {
  readonly accountId?: string;
  readonly bounds?: GeoBounds;
  readonly assetIds?: readonly string[];
}

export type TrackingViewport = Omit<TrackingScope, "accountId">;

/** One page of the Asset List (`GET /tracking/vehicles`): every asset in
 *  scope, devices or not, sorted by name. */
export interface TrackingVehicleQuery {
  readonly accountId?: string;
  readonly search?: string;
  readonly status?: VehicleListStatus;
  readonly page: number;
  readonly limit: number;
}

/** The status counters (`GET /tracking/vehicles/counts`), for the scope and
 *  search -- never narrowed by the status filter. `online` is moving + idle +
 *  stopped. */
export interface TrackingVehicleCounts {
  readonly total: number;
  readonly online: number;
  readonly moving: number;
  readonly idle: number;
  readonly stopped: number;
  readonly offline: number;
  readonly noDevice: number;
}

/** The Asset List cards followed live (`tracking:list:watch`), apart from
 *  the map: their ids (at most `MAX_LIST_WATCH`), and the search and status
 *  tab the counters and removals follow. */
export interface TrackingListWatch {
  readonly assetIds: readonly string[];
  readonly search?: string;
  readonly status?: VehicleListStatus;
}

/** The feed accepts this many watched cards at a time. */
export const MAX_LIST_WATCH = 100;

/** A watch's answer: the cards' current telemetry (those with a position)
 *  and the counters. */
export interface TrackingListState {
  readonly events: readonly VehicleTelemetryEvent[];
  readonly counts: TrackingVehicleCounts;
}

/** Why cards left the list: their status no longer matches the open tab, or
 *  they left the user's scope. */
export type TrackingListRemovalReason = "status" | "scope";

/** A company a Level 1 user can track (`GET /tracking/accounts`). */
export type TrackingAccount = NamedRef;

/** A refused request or session, with the feed's stable code, e.g.
 *  `TRACKING_COMPANY_SCOPE_REQUIRED`, `AUTHENTICATION_REQUIRED`,
 *  `TRACKING_LOAD_FAILED`. The message is already translated. */
export interface TrackingError {
  readonly code: string;
  readonly message: string;
}

/** Everything the feed tells the client, in order. */
export type TrackingStreamEvent =
  /** Replace every vehicle held (a scope's snapshot, or a new stream). */
  | { readonly type: "snapshot"; readonly snapshot: TrackingSnapshot }
  /** Add or replace these vehicles by id. */
  | { readonly type: "vehicles"; readonly vehicles: readonly TrackedVehicle[] }
  /** What the scope holds after the viewport moved: keep these (newer
   *  telemetry wins), drop the rest. */
  | { readonly type: "visible"; readonly vehicles: readonly TrackedVehicle[] }
  /** Drop these: no longer in the user's scope. */
  | { readonly type: "removed"; readonly vehicleIds: readonly string[] }
  | { readonly type: "telemetry"; readonly events: readonly VehicleTelemetryEvent[] }
  // The Asset List's own feed (tracking:list:*): the watched cards wherever
  // they are, whatever the map shows. Never applied to the map's vehicles.
  /** A watch was answered (also after a reconnect): authoritative for the
   *  watched cards, and the counters. */
  | { readonly type: "list-watched"; readonly state: TrackingListState }
  | { readonly type: "list-telemetry"; readonly events: readonly VehicleTelemetryEvent[] }
  /** Cards whose details changed: replace them. */
  | { readonly type: "list-vehicles"; readonly vehicles: readonly TrackedVehicle[] }
  /** Cards to drop at once; the list is then refetched. */
  | {
      readonly type: "list-removed";
      readonly vehicleIds: readonly string[];
      readonly reason: TrackingListRemovalReason;
    }
  | { readonly type: "list-counts"; readonly counts: TrackingVehicleCounts }
  /** A session refused or revoked, or a failed background load. */
  | { readonly type: "error"; readonly error: TrackingError };

export type TrackingConnectionState =
  "connecting" | "connected" | "reconnecting" | "disconnected";
